'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import type { ToolLiveConfig } from '@/lib/tools';

type CanvasPresetId =
  | 'square'
  | 'portrait'
  | 'landscape'
  | 'app-store-portrait'
  | 'app-store-landscape'
  | 'google-play-portrait'
  | 'google-play-landscape';
type DurationPreset = 6 | 10 | 15;
type MotionStyle = 'still' | 'slow-zoom' | 'pan' | 'zoom-pan';
type TransitionStyle = 'cut' | 'fade' | 'slide';
type TemplatePresetId = 'clean-showcase' | 'feature-highlight' | 'brand-close';

type LoadedSlide = {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  previewUrl: string;
  image: HTMLImageElement;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type ProductVideoShowcaseMakerProps = {
  variant?: ToolLiveConfig;
};

type CanvasPreset = {
  id: CanvasPresetId;
  label: string;
  width: number;
  height: number;
  summary: string;
};

type TemplatePreset = {
  id: TemplatePresetId;
  label: string;
  summary: string;
  canvasPresetId: CanvasPresetId;
  duration: DurationPreset;
  motionStyle: MotionStyle;
  transitionStyle: TransitionStyle;
  headline: string;
  badge: string;
  outro: string;
};

type RecorderFormat = {
  mimeType: string;
  extension: 'mp4' | 'webm';
  label: 'MP4' | 'WebM';
  streamMode: 'realtime' | 'request-frame';
};

type VideoToolMode = 'seller' | 'app-promo';

type VideoToolCopy = {
  title: string;
  subtitle: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  uploadHeading: string;
  uploadSubheading: string;
  uploaderEmptyTitle: string;
  uploaderEmptySubtitle: string;
  uploaderReadyLabel: string;
  uploaderReadySummary: string;
  uploaderIdleLabel: string;
  uploaderIdleSummary: string;
  queueSummary: string;
  arrangeHeading: string;
  arrangeSubheading: string;
  reviewLabel: string;
  previewTitle: string;
  previewEmpty: string;
  previewPaused: string;
  previewOverlayTitle: string;
  previewOverlaySubtitle: string;
  sequenceHeading: string;
  sequenceSubheading: string;
  slideCardPrefix: string;
  slideCardSubheading: string;
  slideZoomLabel: string;
  slideHorizontalLabel: string;
  slideVerticalLabel: string;
  styleHeading: string;
  styleSubheading: string;
  coreTitle: string;
  coreSubtitle: string;
  motionTitle: string;
  motionSubtitle: string;
  overlaysTitle: string;
  overlaysSubtitle: string;
  headlineLabel: string;
  headlinePlaceholder: string;
  badgeLabelField: string;
  badgePlaceholder: string;
  outroLabel: string;
  outroPlaceholder: string;
  outroHint: string;
  exportHeading: string;
  exportSubheading: string;
  exportLockedLabel: string;
  exportLockedSubtitle: string;
  exportProgressSuffix: string;
  summarySlidesLabel: string;
  summaryTemplateLabel: string;
  summarySelectedLabel: string;
  summaryEmptySelected: string;
  showScopeNote?: boolean;
  localOnlySubtitle: string;
  scopeTitle: string;
  scopeSubtitle: string;
};

type FfmpegRuntime = {
  ffmpeg: import('@ffmpeg/ffmpeg').FFmpeg;
  fetchFile: typeof import('@ffmpeg/util').fetchFile;
};

let ffmpegRuntimePromise: Promise<FfmpegRuntime> | null = null;

const maxSlides = 8;
const minSlides = 2;
const maxFileSize = 12 * 1024 * 1024;
const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);

const sellerCanvasPresets: CanvasPreset[] = [
  {
    id: 'square',
    label: 'Square',
    width: 1080,
    height: 1080,
    summary: 'Balanced marketplace and gallery videos.',
  },
  {
    id: 'portrait',
    label: 'Portrait',
    width: 1080,
    height: 1920,
    summary: 'Vertical storefront and product-card videos.',
  },
  {
    id: 'landscape',
    label: 'Landscape',
    width: 1920,
    height: 1080,
    summary: 'Wide PDP and storefront showcase videos.',
  },
];

const appPromoCanvasPresets: CanvasPreset[] = [
  {
    id: 'portrait',
    label: '1080 × 1920',
    width: 1080,
    height: 1920,
    summary: 'Vertical promo draft at 30 fps.',
  },
  {
    id: 'square',
    label: '1080 × 1080',
    width: 1080,
    height: 1080,
    summary: 'Square promo format at 30 fps.',
  },
  {
    id: 'landscape',
    label: '1920 × 1080',
    width: 1920,
    height: 1080,
    summary: 'Wide promo format at 30 fps.',
  },
  {
    id: 'app-store-portrait',
    label: 'App Store 886 × 1920',
    width: 886,
    height: 1920,
    summary: 'Vertical App Store preview draft at 30 fps.',
  },
  {
    id: 'app-store-landscape',
    label: 'App Store 1920 × 886',
    width: 1920,
    height: 886,
    summary: 'Landscape App Store preview draft at 30 fps.',
  },
  {
    id: 'google-play-portrait',
    label: 'Google Play 1080 × 1920',
    width: 1080,
    height: 1920,
    summary: 'Vertical Google Play draft at 30 fps.',
  },
  {
    id: 'google-play-landscape',
    label: 'Google Play 1920 × 1080',
    width: 1920,
    height: 1080,
    summary: 'Landscape Google Play draft at 30 fps.',
  },
];

const templatePresets: TemplatePreset[] = [
  {
    id: 'clean-showcase',
    label: 'Clean showcase',
    summary: 'Simple product-only sequence with calm motion and no extra clutter.',
    canvasPresetId: 'square',
    duration: 10,
    motionStyle: 'slow-zoom',
    transitionStyle: 'fade',
    headline: '',
    badge: '',
    outro: '',
  },
  {
    id: 'feature-highlight',
    label: 'Feature highlight',
    summary: 'Short headline and badge for stronger listing callouts.',
    canvasPresetId: 'portrait',
    duration: 10,
    motionStyle: 'zoom-pan',
    transitionStyle: 'slide',
    headline: 'Product highlights',
    badge: 'New',
    outro: '',
  },
  {
    id: 'brand-close',
    label: 'Brand close',
    summary: 'Simple product sequence that ends on a short brand card.',
    canvasPresetId: 'landscape',
    duration: 15,
    motionStyle: 'slow-zoom',
    transitionStyle: 'fade',
    headline: '',
    badge: '',
    outro: 'Brand',
  },
];

const sellerVideoCopy: VideoToolCopy = {
  title: 'Build a short product showcase video from photos in a few local steps',
  subtitle:
    'Upload 2 to 8 product images, choose a simple showcase template, adjust the order and framing, and export one short listing-ready video without server-side rendering.',
  step1: 'Add 2 to 8 product images',
  step2: 'Set the order and framing',
  step3: 'Choose the motion and overlay presets',
  step4: 'Render one short showcase video locally',
  uploadHeading: 'Upload',
  uploadSubheading: 'Build a small showcase sequence that stays on this device.',
  uploaderEmptyTitle: 'Start with product photos',
  uploaderEmptySubtitle: 'Drop in 2 to 8 images for a short listing or storefront video.',
  uploaderReadyLabel: 'product images ready for the sequence',
  uploaderReadySummary: 'slides',
  uploaderIdleLabel: 'Upload product images',
  uploaderIdleSummary: 'images',
  queueSummary: 'slides loaded',
  arrangeHeading: 'Arrange',
  arrangeSubheading: 'Preview the sequence, reorder slides, and refine one frame if needed.',
  reviewLabel: 'Click to review',
  previewTitle: 'Preview',
  previewEmpty: 'Your short product showcase preview appears here',
  previewPaused: 'Paused on selected slide',
  previewOverlayTitle: 'Short product showcase preview',
  previewOverlaySubtitle:
    'Upload product images to watch the real sequence, motion, and transitions here.',
  sequenceHeading: 'Sequence',
  sequenceSubheading: 'Select a slide, then move it earlier or later in the showcase.',
  slideCardPrefix: 'Slide',
  slideCardSubheading: 'Fine-tune the framing or swap this shot if it needs a cleaner crop.',
  slideZoomLabel: 'Slide zoom',
  slideHorizontalLabel: 'Left / right',
  slideVerticalLabel: 'Up / down',
  styleHeading: 'Style',
  styleSubheading:
    'Start from one seller preset, then adjust only the settings that change the final video.',
  coreTitle: 'Core settings',
  coreSubtitle: 'These presets define the final video shape and pacing.',
  motionTitle: 'Motion and transitions',
  motionSubtitle: 'Pick the movement that fits the product best.',
  overlaysTitle: 'Optional overlays',
  overlaysSubtitle: 'Add a short headline, a badge, or a simple closing card.',
  headlineLabel: 'Headline',
  headlinePlaceholder: 'Short product headline',
  badgeLabelField: 'Badge',
  badgePlaceholder: 'New / Sale / $29',
  outroLabel: 'Brand outro',
  outroPlaceholder: 'Optional closing card',
  outroHint: 'Leave blank if you want to end on the last product slide.',
  exportHeading: 'Export',
  exportSubheading: 'Render one short storefront-ready video with the current sequence and style.',
  exportLockedLabel: 'Add 2 images to unlock export',
  exportLockedSubtitle:
    'This builder needs at least two product images before it can render a showcase video.',
  exportProgressSuffix: 'showcase',
  summarySlidesLabel: 'Slides',
  summaryTemplateLabel: 'Template',
  summarySelectedLabel: 'Selected slide',
  summaryEmptySelected: 'Choose a slide',
  showScopeNote: true,
  localOnlySubtitle: 'Preview, sequencing, and showcase rendering stay in the browser.',
  scopeTitle: 'Narrow scope',
  scopeSubtitle: 'This is a fast product showcase builder, not a full timeline or audio editor.',
};

const appPromoCopy: VideoToolCopy = {
  title: 'Build a short app promo video from screenshots in a few local steps',
  subtitle:
    'Upload screenshots, arrange the feature sequence, choose a lightweight promo style, and export one short store-ready draft locally.',
  step1: 'Add 2 to 8 screenshots',
  step2: 'Arrange the feature sequence',
  step3: 'Choose promo motion and overlays',
  step4: 'Render one short promo video locally',
  uploadHeading: 'Upload screenshots',
  uploadSubheading: 'Build a short promo draft that stays on this device.',
  uploaderEmptyTitle: 'Start with app screenshots',
  uploaderEmptySubtitle: 'Drop in 2 to 8 screenshots for a short app promo or store draft video.',
  uploaderReadyLabel: 'screenshots ready for the promo sequence',
  uploaderReadySummary: 'screens',
  uploaderIdleLabel: 'Upload app screenshots',
  uploaderIdleSummary: 'screenshots',
  queueSummary: 'screens loaded',
  arrangeHeading: 'Arrange feature sequence',
  arrangeSubheading:
    'Preview the screen order, reorder key moments, and tighten the current frame.',
  reviewLabel: 'Review screen',
  previewTitle: 'Promo preview',
  previewEmpty: 'Your short app promo preview appears here',
  previewPaused: 'Paused on selected screen',
  previewOverlayTitle: 'Short app promo preview',
  previewOverlaySubtitle:
    'Upload screenshots to preview the real sequence, pacing, and transitions here.',
  sequenceHeading: 'Screenshot sequence',
  sequenceSubheading: 'Select a screen, then move it earlier or later in the promo flow.',
  slideCardPrefix: 'Screen',
  slideCardSubheading:
    'Adjust the framing or swap this screenshot if the feature story reads better another way.',
  slideZoomLabel: 'Screenshot zoom',
  slideHorizontalLabel: 'Left / right',
  slideVerticalLabel: 'Up / down',
  styleHeading: 'Style promo',
  styleSubheading: 'Start from one promo preset, then adjust only what changes the final video.',
  coreTitle: 'Canvas and pacing',
  coreSubtitle: 'These presets define the frame, timing, and overall promo shape.',
  motionTitle: 'Motion and transitions',
  motionSubtitle: 'Pick the movement that fits the screenshot flow best.',
  overlaysTitle: 'Optional promo text',
  overlaysSubtitle:
    'Turn these on only if the promo needs a title, a short label, or a closing screen.',
  headlineLabel: 'Headline',
  headlinePlaceholder: 'One clear product benefit',
  badgeLabelField: 'Badge or step label',
  badgePlaceholder: 'New feature / Step 1 / Faster sync',
  outroLabel: 'Closing card',
  outroPlaceholder: 'App name / Try it today',
  outroHint: 'Leave this empty to end on the last screenshot without a closing card.',
  exportHeading: 'Export video',
  exportSubheading: 'Render one short promo draft with the current sequence and style.',
  exportLockedLabel: 'Add 2 screenshots to unlock export',
  exportLockedSubtitle:
    'This builder needs at least two screenshots before it can render a short promo video.',
  exportProgressSuffix: 'promo',
  summarySlidesLabel: 'Screens',
  summaryTemplateLabel: 'Promo preset',
  summarySelectedLabel: 'Selected screen',
  summaryEmptySelected: 'Choose a screen',
  showScopeNote: false,
  localOnlySubtitle: 'Preview, sequencing, and promo rendering stay in the browser.',
  scopeTitle: 'Draft builder',
  scopeSubtitle:
    'This is a fast screenshot-to-promo utility, not an official App Store preview recorder or full video editor.',
};

function getVideoTemplatePresets(mode: VideoToolMode): TemplatePreset[] {
  if (mode === 'app-promo') {
    return [
      {
        id: 'clean-showcase',
        label: 'Feature walkthrough',
        summary: 'Clean screenshot sequence with calm motion and no extra clutter.',
        canvasPresetId: 'app-store-portrait',
        duration: 10,
        motionStyle: 'slow-zoom',
        transitionStyle: 'fade',
        headline: '',
        badge: '',
        outro: '',
      },
      {
        id: 'feature-highlight',
        label: 'Launch highlight',
        summary: 'App-focused pacing and movement for a stronger store or launch promo draft.',
        canvasPresetId: 'app-store-portrait',
        duration: 10,
        motionStyle: 'zoom-pan',
        transitionStyle: 'slide',
        headline: '',
        badge: '',
        outro: '',
      },
      {
        id: 'brand-close',
        label: 'Store close',
        summary: 'Simple screenshot sequence that leaves room for an optional closing card.',
        canvasPresetId: 'landscape',
        duration: 15,
        motionStyle: 'slow-zoom',
        transitionStyle: 'fade',
        headline: '',
        badge: '',
        outro: '',
      },
    ];
  }

  return templatePresets;
}

function getCanvasPresets(mode: VideoToolMode) {
  return mode === 'app-promo' ? appPromoCanvasPresets : sellerCanvasPresets;
}

function getCanvasSummary(mode: VideoToolMode, presetId: CanvasPresetId) {
  if (mode === 'app-promo') {
    switch (presetId) {
      case 'square':
        return 'Square promo format at 30 fps.';
      case 'portrait':
        return 'Vertical promo draft at 30 fps.';
      case 'app-store-portrait':
        return 'Vertical App Store preview draft at 30 fps.';
      case 'app-store-landscape':
        return 'Landscape App Store preview draft at 30 fps.';
      case 'google-play-portrait':
        return 'Vertical Google Play draft at 30 fps.';
      case 'google-play-landscape':
        return 'Landscape Google Play draft at 30 fps.';
      default:
        return 'Wide promo format at 30 fps.';
    }
  }

  return sellerCanvasPresets.find((preset) => preset.id === presetId)?.summary ?? '';
}

function getMotionSummary(mode: VideoToolMode, style: MotionStyle) {
  if (mode === 'app-promo') {
    switch (style) {
      case 'still':
        return 'Static screen hold.';
      case 'slow-zoom':
        return 'Gentle focus on each screen.';
      case 'pan':
        return 'Subtle lateral movement.';
      default:
        return 'Slow zoom with light drift.';
    }
  }

  switch (style) {
    case 'still':
      return 'No camera movement.';
    case 'slow-zoom':
      return 'Gentle push-in across each slide.';
    case 'pan':
      return 'Slow lateral movement.';
    default:
      return 'A little zoom with subtle drift.';
  }
}

function getTransitionSummary(mode: VideoToolMode, style: TransitionStyle) {
  if (mode === 'app-promo') {
    switch (style) {
      case 'cut':
        return 'Fastest step change.';
      case 'fade':
        return 'Soft screen handoff.';
      default:
        return 'Motion between screens.';
    }
  }

  switch (style) {
    case 'cut':
      return 'Fastest and most direct.';
    case 'fade':
      return 'Softer product handoff.';
    default:
      return 'More motion between slides.';
  }
}

function getTemplateById(templateId: string | undefined) {
  return templatePresets.find((preset) => preset.id === templateId) ?? templatePresets[0]!;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `showcase-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function triggerDownload(url: string, name: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

async function yieldToBrowser() {
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
}

async function wait(ms: number) {
  await new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The browser could not create a local video frame.'));
      },
      type,
      quality,
    );
  });
}

async function loadImageElement(url: string) {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () =>
      reject(new Error('This image format is not supported in your browser yet.'));
    nextImage.src = url;
  });

  return image;
}

async function prepareSlide(file: File): Promise<LoadedSlide> {
  const previewUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(previewUrl);

    return {
      id: createId(),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      width: image.naturalWidth,
      height: image.naturalHeight,
      previewUrl,
      image,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    };
  } catch (error) {
    URL.revokeObjectURL(previewUrl);
    throw error;
  }
}

function supportsRecorderCandidate(candidate: RecorderFormat) {
  if (
    typeof window === 'undefined' ||
    typeof MediaRecorder === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return false;
  }

  try {
    if (!MediaRecorder.isTypeSupported(candidate.mimeType)) {
      return false;
    }

    const probeCanvas = document.createElement('canvas');
    probeCanvas.width = 2;
    probeCanvas.height = 2;

    if (typeof probeCanvas.captureStream !== 'function') {
      return false;
    }

    const stream = probeCanvas.captureStream(candidate.streamMode === 'request-frame' ? 0 : 1);
    const recorder = new MediaRecorder(stream, { mimeType: candidate.mimeType });
    recorder.stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

function chooseWebmRecorderFormats(): RecorderFormat[] {
  const candidates: RecorderFormat[] = [
    {
      mimeType: 'video/webm;codecs=vp9',
      extension: 'webm',
      label: 'WebM',
      streamMode: 'request-frame',
    },
    {
      mimeType: 'video/webm;codecs=vp8',
      extension: 'webm',
      label: 'WebM',
      streamMode: 'request-frame',
    },
    { mimeType: 'video/webm', extension: 'webm', label: 'WebM', streamMode: 'request-frame' },
  ];

  return candidates.filter((candidate) => supportsRecorderCandidate(candidate));
}

function getPreferredVideoBitrate(width: number, height: number, frameRate: number) {
  const pixelsPerSecond = width * height * frameRate;
  return clamp(Math.round(pixelsPerSecond * 0.15), 2_000_000, 8_500_000);
}

async function chooseMp4EncoderConfig(
  width: number,
  height: number,
  frameRate: number,
): Promise<VideoEncoderConfig | null> {
  if (
    typeof window === 'undefined' ||
    typeof VideoEncoder === 'undefined' ||
    typeof VideoFrame === 'undefined'
  ) {
    return null;
  }

  const bitrate = getPreferredVideoBitrate(width, height, frameRate);
  const candidates: VideoEncoderConfig[] = [
    {
      codec: 'avc1.42E01E',
      width,
      height,
      bitrate,
      bitrateMode: 'variable',
      framerate: frameRate,
      hardwareAcceleration: 'prefer-hardware',
      latencyMode: 'quality',
      avc: { format: 'avc' },
    },
    {
      codec: 'avc1.4D401F',
      width,
      height,
      bitrate,
      bitrateMode: 'variable',
      framerate: frameRate,
      hardwareAcceleration: 'prefer-hardware',
      latencyMode: 'quality',
      avc: { format: 'avc' },
    },
  ];

  for (const candidate of candidates) {
    try {
      const support = await VideoEncoder.isConfigSupported(candidate);

      if (support.supported) {
        return support.config ?? candidate;
      }
    } catch {
      continue;
    }
  }

  return null;
}

function canUseFfmpegFallback() {
  return (
    typeof window !== 'undefined' &&
    typeof Worker !== 'undefined' &&
    typeof document !== 'undefined'
  );
}

async function getFfmpegRuntime(): Promise<FfmpegRuntime> {
  if (!ffmpegRuntimePromise) {
    ffmpegRuntimePromise = (async () => {
      const [{ FFmpeg }, { fetchFile, toBlobURL }] = await Promise.all([
        import('@ffmpeg/ffmpeg'),
        import('@ffmpeg/util'),
      ]);
      const ffmpeg = new FFmpeg();
      const baseUrl = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.9/dist/umd';
      // The single-threaded UMD build ships no ffmpeg-core.worker.js: that file only
      // exists in the multi-threaded core. Asking for it 404s on every single run,
      // and because a 404 is a perfectly good HTTP response, toBlobURL wraps the
      // error page in a blob URL and hands it over as if it were a worker. Nothing
      // breaks, since the ST core never reads it, so this cost one wasted round trip
      // per conversion and a console error that made every tool look broken.
      const [coreURL, wasmURL] = await Promise.all([
        toBlobURL(`${baseUrl}/ffmpeg-core.js`, 'text/javascript'),
        toBlobURL(`${baseUrl}/ffmpeg-core.wasm`, 'application/wasm'),
      ]);

      await ffmpeg.load({ coreURL, wasmURL });

      return { ffmpeg, fetchFile };
    })().catch((error) => {
      ffmpegRuntimePromise = null;
      throw error;
    });
  }

  return ffmpegRuntimePromise;
}

async function loadRecordedVideoDuration(blob: Blob) {
  if (typeof document === 'undefined') {
    return null;
  }

  return await new Promise<number | null>((resolve) => {
    const objectUrl = URL.createObjectURL(blob);
    const video = document.createElement('video');
    let settled = false;

    const finish = (duration: number | null) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      video.pause();
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };

    const readDuration = () => {
      const { duration } = video;

      if (Number.isFinite(duration) && duration > 0) {
        finish(duration);
        return true;
      }

      return false;
    };

    const timeoutId = window.setTimeout(() => finish(null), 5000);

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      if (readDuration()) {
        return;
      }

      try {
        video.currentTime = 10_000;
      } catch {
        finish(null);
      }
    };
    video.ontimeupdate = () => {
      readDuration();
    };
    video.onerror = () => finish(null);
    video.src = objectUrl;
  });
}

function isRecordedDurationAcceptable(duration: number | null, targetDuration: number) {
  if (!duration || !Number.isFinite(duration)) {
    return false;
  }

  const minDuration = Math.max(0.9, targetDuration * 0.82);
  const maxDuration = targetDuration + 1.25;
  return duration >= minDuration && duration <= maxDuration;
}

function getMotionLabel(style: MotionStyle) {
  switch (style) {
    case 'still':
      return 'Still';
    case 'slow-zoom':
      return 'Slow zoom';
    case 'pan':
      return 'Pan';
    default:
      return 'Zoom + pan';
  }
}

function getTransitionLabel(style: TransitionStyle) {
  switch (style) {
    case 'cut':
      return 'Cut';
    case 'fade':
      return 'Fade';
    default:
      return 'Slide';
  }
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

function applyTemplate(templateId: TemplatePresetId) {
  return templatePresets.find((preset) => preset.id === templateId) ?? templatePresets[0]!;
}

function drawImageSlide(
  context: CanvasRenderingContext2D,
  slide: LoadedSlide,
  progress: number,
  canvasWidth: number,
  canvasHeight: number,
  motionStyle: MotionStyle,
) {
  const image = slide.image;
  const baseScale = Math.max(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
  let motionZoom = 1;
  let motionOffsetX = 0;
  let motionOffsetY = 0;

  if (motionStyle === 'slow-zoom') {
    motionZoom = 1 + progress * 0.08;
  } else if (motionStyle === 'pan') {
    motionZoom = 1.04;
    motionOffsetX = (progress - 0.5) * 0.12;
  } else if (motionStyle === 'zoom-pan') {
    motionZoom = 1.02 + progress * 0.1;
    motionOffsetX = (progress - 0.5) * 0.1;
    motionOffsetY = (0.5 - progress) * 0.06;
  }

  const scale = baseScale * slide.zoom * motionZoom;
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  let x = (canvasWidth - width) / 2 + (slide.offsetX + motionOffsetX) * canvasWidth;
  let y = (canvasHeight - height) / 2 + (slide.offsetY + motionOffsetY) * canvasHeight;

  if (width > canvasWidth) {
    x = clamp(x, canvasWidth - width, 0);
  } else {
    x = (canvasWidth - width) / 2;
  }

  if (height > canvasHeight) {
    y = clamp(y, canvasHeight - height, 0);
  } else {
    y = (canvasHeight - height) / 2;
  }

  context.drawImage(image, x, y, width, height);
}

function drawOverlay(
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  headline: string,
  badge: string,
) {
  if (!headline && !badge) {
    return;
  }

  context.save();

  if (headline) {
    const gradient = context.createLinearGradient(0, 0, 0, canvasHeight * 0.34);
    gradient.addColorStop(0, 'rgba(9, 12, 19, 0.68)');
    gradient.addColorStop(1, 'rgba(9, 12, 19, 0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvasWidth, canvasHeight * 0.34);
  }

  if (badge) {
    context.font = `700 ${Math.round(canvasWidth * 0.032)}px "Instrument Sans", sans-serif`;
    const badgePaddingX = canvasWidth * 0.018;
    const badgePaddingY = canvasHeight * 0.012;
    const badgeWidth = context.measureText(badge).width + badgePaddingX * 2;
    const badgeHeight = canvasHeight * 0.072;
    const badgeX = canvasWidth * 0.05;
    const badgeY = canvasHeight * 0.06;
    context.fillStyle = 'rgba(113, 102, 255, 0.94)';
    drawRoundedRect(context, badgeX, badgeY, badgeWidth, badgeHeight, badgeHeight / 2);
    context.fill();
    context.fillStyle = '#f8f8ff';
    context.textBaseline = 'middle';
    context.fillText(badge, badgeX + badgePaddingX, badgeY + badgeHeight / 2 + 1);
  }

  if (headline) {
    context.fillStyle = '#ffffff';
    context.font = `700 ${Math.round(canvasWidth * 0.05)}px "Instrument Sans", sans-serif`;
    context.textBaseline = 'top';
    const maxWidth = canvasWidth * 0.82;
    const words = headline.trim().split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;

      if (context.measureText(nextLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = nextLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    const startY = badge ? canvasHeight * 0.15 : canvasHeight * 0.08;

    lines.slice(0, 2).forEach((line, index) => {
      context.fillText(line, canvasWidth * 0.05, startY + index * canvasHeight * 0.065);
    });
  }

  context.restore();
}

function drawOutro(
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  brandOutro: string,
) {
  const gradient = context.createLinearGradient(0, 0, canvasWidth, canvasHeight);
  gradient.addColorStop(0, '#141924');
  gradient.addColorStop(1, '#1b2230');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvasWidth, canvasHeight);

  context.fillStyle = 'rgba(255, 255, 255, 0.1)';
  drawRoundedRect(
    context,
    canvasWidth * 0.09,
    canvasHeight * 0.18,
    canvasWidth * 0.82,
    canvasHeight * 0.64,
    canvasHeight * 0.05,
  );
  context.fill();

  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `700 ${Math.round(canvasWidth * 0.064)}px "Instrument Sans", sans-serif`;
  context.fillText(brandOutro || 'Brand', canvasWidth / 2, canvasHeight * 0.47);
  context.font = `500 ${Math.round(canvasWidth * 0.026)}px "Instrument Sans", sans-serif`;
  context.fillStyle = 'rgba(255, 255, 255, 0.78)';
  context.fillText('Product showcase', canvasWidth / 2, canvasHeight * 0.6);
}

function renderShowcaseFrame(
  context: CanvasRenderingContext2D,
  timeSec: number,
  slides: LoadedSlide[],
  canvasPreset: CanvasPreset,
  totalDuration: number,
  motionStyle: MotionStyle,
  transitionStyle: TransitionStyle,
  backgroundFill: string,
  headline: string,
  badge: string,
  brandOutro: string,
) {
  const canvasWidth = canvasPreset.width;
  const canvasHeight = canvasPreset.height;
  context.clearRect(0, 0, canvasWidth, canvasHeight);
  context.fillStyle = backgroundFill;
  context.fillRect(0, 0, canvasWidth, canvasHeight);

  const effectiveSlides = slides.length + (brandOutro.trim() ? 1 : 0);

  if (!effectiveSlides) {
    return;
  }

  const clampedTime = clamp(timeSec, 0, Math.max(totalDuration - 0.001, 0));
  const segmentDuration = totalDuration / effectiveSlides;
  const transitionDuration =
    transitionStyle === 'cut' ? 0 : Math.min(0.45, Math.max(segmentDuration * 0.24, 0.18));
  const slideIndex = clamp(Math.floor(clampedTime / segmentDuration), 0, effectiveSlides - 1);
  const segmentStart = slideIndex * segmentDuration;
  const localTime = clampedTime - segmentStart;
  const progress = clamp(localTime / segmentDuration, 0, 1);
  const hasOutro = Boolean(brandOutro.trim());
  const isOutro = hasOutro && slideIndex === effectiveSlides - 1;

  const drawSlideByIndex = (index: number, alpha: number, offsetDirection: number) => {
    context.save();
    context.globalAlpha = alpha;

    if (transitionStyle === 'slide' && transitionDuration > 0) {
      context.translate(offsetDirection * canvasWidth, 0);
    }

    if (hasOutro && index === effectiveSlides - 1) {
      drawOutro(context, canvasWidth, canvasHeight, brandOutro);
    } else {
      const slide = slides[index];

      if (!slide) {
        context.restore();
        return;
      }

      drawImageSlide(context, slide, progress, canvasWidth, canvasHeight, motionStyle);
      drawOverlay(context, canvasWidth, canvasHeight, headline, badge);
    }

    context.restore();
  };

  const inTransition =
    transitionDuration > 0 &&
    localTime > segmentDuration - transitionDuration &&
    slideIndex < effectiveSlides - 1;
  const transitionProgress = inTransition
    ? clamp((localTime - (segmentDuration - transitionDuration)) / transitionDuration, 0, 1)
    : 0;

  if (!inTransition) {
    drawSlideByIndex(slideIndex, 1, 0);
    return;
  }

  if (transitionStyle === 'fade') {
    drawSlideByIndex(slideIndex, 1 - transitionProgress, 0);
    drawSlideByIndex(slideIndex + 1, transitionProgress, 0);
    return;
  }

  if (transitionStyle === 'slide') {
    drawSlideByIndex(slideIndex, 1, -transitionProgress);
    drawSlideByIndex(slideIndex + 1, 1, 1 - transitionProgress);
    return;
  }

  drawSlideByIndex(slideIndex + 1, 1, 0);
}

export default function BrowserProductVideoShowcaseMaker({
  variant,
}: ProductVideoShowcaseMakerProps) {
  const toolMode: VideoToolMode =
    variant?.variant === 'app-promo-video-maker' ? 'app-promo' : 'seller';
  const copy = toolMode === 'app-promo' ? appPromoCopy : sellerVideoCopy;
  const availableTemplatePresets = useMemo(() => getVideoTemplatePresets(toolMode), [toolMode]);
  const availableCanvasPresets = useMemo(() => getCanvasPresets(toolMode), [toolMode]);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const previewRafRef = useRef<number | null>(null);
  const previewStartRef = useRef<number>(0);
  const slidesRef = useRef<LoadedSlide[]>([]);
  const initialTemplate =
    availableTemplatePresets.find(
      (preset) => preset.id === variant?.defaultVideoTemplatePresetId,
    ) ?? availableTemplatePresets[0]!;
  const [slides, setSlides] = useState<LoadedSlide[]>([]);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);
  const [templatePresetId, setTemplatePresetId] = useState<TemplatePresetId>(initialTemplate.id);
  const [canvasPresetId, setCanvasPresetId] = useState<CanvasPresetId>(
    variant?.defaultCanvasPresetId ?? initialTemplate.canvasPresetId,
  );
  const [durationPreset, setDurationPreset] = useState<DurationPreset>(
    variant?.defaultVideoDuration ?? initialTemplate.duration,
  );
  const [motionStyle, setMotionStyle] = useState<MotionStyle>(
    variant?.defaultVideoMotionStyle ?? initialTemplate.motionStyle,
  );
  const [transitionStyle, setTransitionStyle] = useState<TransitionStyle>(
    variant?.defaultVideoTransitionStyle ?? initialTemplate.transitionStyle,
  );
  const [headline, setHeadline] = useState(
    variant?.defaultVideoHeadline ?? initialTemplate.headline,
  );
  const [badge, setBadge] = useState(variant?.defaultVideoBadge ?? initialTemplate.badge);
  const [brandOutro, setBrandOutro] = useState(
    variant?.defaultVideoBrandOutro ?? initialTemplate.outro,
  );
  const [backgroundFill, setBackgroundFill] = useState('#f1f4f7');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(toolMode !== 'app-promo');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [downloadLabel, setDownloadLabel] = useState<string>('Video');
  const [recorderLabel, setRecorderLabel] = useState<'MP4' | 'WebM' | 'Unavailable'>('Unavailable');

  const activeCanvasPreset =
    availableCanvasPresets.find((preset) => preset.id === canvasPresetId) ??
    availableCanvasPresets[0]!;
  const selectedSlide = slides.find((slide) => slide.id === selectedSlideId) ?? slides[0] ?? null;
  const hasSlides = slides.length > 0;
  const selectedSlideIndex = selectedSlide
    ? Math.max(
        0,
        slides.findIndex((slide) => slide.id === selectedSlide.id),
      )
    : -1;
  const totalDuration = durationPreset;
  const effectiveSlideCount = slides.length + (brandOutro.trim() ? 1 : 0);
  const slideDuration = effectiveSlideCount ? totalDuration / effectiveSlideCount : 0;
  const overlaysEnabled = Boolean(headline.trim() || badge.trim() || brandOutro.trim());
  const canExport = slides.length >= minSlides && !isPreparing && !isExporting;
  const [previewViewportSize, setPreviewViewportSize] = useState(() =>
    toolMode === 'app-promo' ? { width: 960, height: 960 } : { width: 960, height: 560 },
  );
  const previewSize = useMemo(
    () =>
      getContainSize(
        activeCanvasPreset.width,
        activeCanvasPreset.height,
        previewViewportSize.width,
        previewViewportSize.height,
      ),
    [activeCanvasPreset, previewViewportSize],
  );

  useLayoutEffect(() => {
    const node = previewFrameRef.current;

    if (!node || typeof window === 'undefined') {
      return;
    }

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const styles = window.getComputedStyle(node);
      const width =
        rect.width - parseFloat(styles.paddingLeft || '0') - parseFloat(styles.paddingRight || '0');
      const height =
        rect.height -
        parseFloat(styles.paddingTop || '0') -
        parseFloat(styles.paddingBottom || '0');

      if (width <= 0 || height <= 0) {
        return;
      }

      setPreviewViewportSize((current) =>
        Math.abs(current.width - width) < 1 && Math.abs(current.height - height) < 1
          ? current
          : { width, height },
      );
    };

    updateSize();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateSize());
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [toolMode]);

  useEffect(() => {
    let cancelled = false;

    const detectPreferredExport = async () => {
      const mp4Config = await chooseMp4EncoderConfig(
        activeCanvasPreset.width,
        activeCanvasPreset.height,
        30,
      );

      if (cancelled) {
        return;
      }

      if (mp4Config) {
        setRecorderLabel('MP4');
        setDownloadLabel('MP4');
        return;
      }

      if (canUseFfmpegFallback()) {
        setRecorderLabel('MP4');
        setDownloadLabel('MP4');
        return;
      }

      const webmRecorderFormat = chooseWebmRecorderFormats()[0] ?? null;
      setRecorderLabel(webmRecorderFormat?.label ?? 'Unavailable');
      setDownloadLabel(webmRecorderFormat?.label ?? 'Video');
    };

    void detectPreferredExport();

    return () => {
      cancelled = true;
    };
  }, [activeCanvasPreset]);

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  useEffect(() => {
    return () => {
      if (previewRafRef.current !== null) {
        cancelAnimationFrame(previewRafRef.current);
      }

      slidesRef.current.forEach((slide) => {
        URL.revokeObjectURL(slide.previewUrl);
      });
    };
  }, []);

  useLayoutEffect(() => {
    const canvas = previewCanvasRef.current;

    if (!canvas) {
      return;
    }

    canvas.width = activeCanvasPreset.width;
    canvas.height = activeCanvasPreset.height;
    canvas.style.width = `${previewSize.width}px`;
    canvas.style.height = `${previewSize.height}px`;
  }, [activeCanvasPreset, hasSlides, previewSize]);

  useEffect(() => {
    const canvas = previewCanvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    if (previewRafRef.current !== null) {
      cancelAnimationFrame(previewRafRef.current);
      previewRafRef.current = null;
    }

    if (!slides.length) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    if (!isPreviewPlaying) {
      const selectedIndex = selectedSlide
        ? Math.max(
            0,
            slides.findIndex((slide) => slide.id === selectedSlide.id),
          )
        : 0;
      renderShowcaseFrame(
        context,
        selectedIndex * slideDuration,
        slides,
        activeCanvasPreset,
        totalDuration,
        motionStyle,
        transitionStyle,
        backgroundFill,
        headline,
        badge,
        brandOutro,
      );
      return;
    }

    renderShowcaseFrame(
      context,
      0,
      slides,
      activeCanvasPreset,
      totalDuration,
      motionStyle,
      transitionStyle,
      backgroundFill,
      headline,
      badge,
      brandOutro,
    );

    previewStartRef.current = performance.now();

    const tick = (timestamp: number) => {
      const elapsed = ((timestamp - previewStartRef.current) / 1000) % totalDuration;
      renderShowcaseFrame(
        context,
        elapsed,
        slides,
        activeCanvasPreset,
        totalDuration,
        motionStyle,
        transitionStyle,
        backgroundFill,
        headline,
        badge,
        brandOutro,
      );
      previewRafRef.current = requestAnimationFrame(tick);
    };

    previewRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (previewRafRef.current !== null) {
        cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
    };
  }, [
    activeCanvasPreset,
    badge,
    backgroundFill,
    brandOutro,
    headline,
    isPreviewPlaying,
    motionStyle,
    selectedSlide,
    slideDuration,
    slides,
    totalDuration,
    transitionStyle,
  ]);

  useEffect(() => {
    if (!slides.length) {
      setSelectedSlideId(null);
      return;
    }

    setSelectedSlideId((current) => current ?? slides[0]!.id);
  }, [slides]);

  function resetExportState() {
    setErrorMessage(null);
    setCreditMessage(null);
    setExportProgress(0);
  }

  function applyTemplatePreset(nextTemplateId: TemplatePresetId) {
    const preset =
      availableTemplatePresets.find((templatePreset) => templatePreset.id === nextTemplateId) ??
      availableTemplatePresets[0]!;
    setTemplatePresetId(preset.id);
    setCanvasPresetId(preset.canvasPresetId);
    setDurationPreset(preset.duration);
    setMotionStyle(preset.motionStyle);
    setTransitionStyle(preset.transitionStyle);
    setHeadline(preset.headline);
    setBadge(preset.badge);
    setBrandOutro(preset.outro);
    resetExportState();
  }

  async function handleIncomingFiles(fileList: FileList | File[] | null, targetId?: string | null) {
    setErrorMessage(null);
    setCreditMessage(null);

    if (!fileList?.length || isExporting) {
      return;
    }

    const nextFiles = Array.from(fileList);
    const validFiles = nextFiles.filter(
      (file) => supportedTypes.has(file.type) && file.size <= maxFileSize,
    );

    if (!validFiles.length) {
      setErrorMessage(
        `Choose PNG, JPG, JPEG, WebP, GIF, or BMP files up to ${Math.round(maxFileSize / (1024 * 1024))} MB each.`,
      );
      return;
    }

    const allowedCount = targetId ? 1 : Math.max(0, maxSlides - slides.length);

    if (!allowedCount) {
      setErrorMessage(`This builder supports up to ${maxSlides} product images.`);
      return;
    }

    if (validFiles.length > allowedCount) {
      setErrorMessage(
        targetId
          ? 'Only one replacement image can be used for a slide.'
          : `Some files were skipped because the builder supports up to ${maxSlides} images.`,
      );
    }

    setIsPreparing(true);

    try {
      const preparedSlides: LoadedSlide[] = [];

      for (const file of validFiles.slice(0, allowedCount)) {
        preparedSlides.push(await prepareSlide(file));
        await yieldToBrowser();
      }

      if (targetId && preparedSlides[0]) {
        setSlides((currentSlides) =>
          currentSlides.map((slide) => {
            if (slide.id !== targetId) {
              return slide;
            }

            URL.revokeObjectURL(slide.previewUrl);
            return {
              ...preparedSlides[0]!,
              id: targetId,
              zoom: slide.zoom,
              offsetX: slide.offsetX,
              offsetY: slide.offsetY,
            };
          }),
        );
        setSelectedSlideId(targetId);
      } else if (preparedSlides.length) {
        setSlides((currentSlides) => [...currentSlides, ...preparedSlides]);
        setSelectedSlideId((current) => current ?? preparedSlides[0]!.id);
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The images could not be prepared locally.',
      );
    } finally {
      setIsPreparing(false);
      setReplaceTargetId(null);
    }
  }

  function moveSlide(slideId: string, direction: -1 | 1) {
    setSlides((currentSlides) => {
      const index = currentSlides.findIndex((slide) => slide.id === slideId);

      if (index < 0) {
        return currentSlides;
      }

      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= currentSlides.length) {
        return currentSlides;
      }

      const nextSlides = [...currentSlides];
      const [target] = nextSlides.splice(index, 1);
      nextSlides.splice(nextIndex, 0, target!);
      return nextSlides;
    });
    resetExportState();
  }

  function removeSlide(slideId: string) {
    if (isExporting) {
      return;
    }

    setSlides((currentSlides) => {
      const target = currentSlides.find((slide) => slide.id === slideId);

      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }

      const nextSlides = currentSlides.filter((slide) => slide.id !== slideId);
      setSelectedSlideId((current) =>
        current === slideId ? (nextSlides[0]?.id ?? null) : current,
      );
      return nextSlides;
    });
    resetExportState();
  }

  function clearAll() {
    if (isExporting) {
      return;
    }

    slides.forEach((slide) => URL.revokeObjectURL(slide.previewUrl));
    setSlides([]);
    setSelectedSlideId(null);
    resetExportState();
  }

  function updateSelectedSlide(patch: Partial<Pick<LoadedSlide, 'zoom' | 'offsetX' | 'offsetY'>>) {
    if (!selectedSlide) {
      return;
    }

    setSlides((currentSlides) =>
      currentSlides.map((slide) =>
        slide.id === selectedSlide.id
          ? {
              ...slide,
              ...patch,
            }
          : slide,
      ),
    );
    resetExportState();
  }

  async function authorizeUsage() {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const idToken = await getToolAuthToken();

    if (idToken) {
      headers.authorization = `Bearer ${idToken}`;
    }

    const usageResponse = await fetch('/api/tools/usage', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        toolSlug: variant?.toolSlug ?? 'product-video-showcase-maker',
      }),
    });
    const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
      success?: boolean;
      data?: {
        credits?: {
          plan?: 'guest' | 'free' | 'pro';
          remaining?: number | null;
          windowLabel?: string;
        };
      };
      error?: unknown;
    };

    if (!usageResponse.ok || !usagePayload.success) {
      const message =
        typeof usagePayload.error === 'object' &&
        usagePayload.error &&
        'message' in usagePayload.error
          ? String((usagePayload.error as { message?: unknown }).message)
          : typeof usagePayload.error === 'string'
            ? usagePayload.error
            : 'Credits are not available for another local export right now.';
      throw new Error(message);
    }

    return usagePayload;
  }

  async function exportVideo() {
    if (slides.length < minSlides) {
      setErrorMessage(`Add at least ${minSlides} product images before exporting.`);
      return;
    }

    const exportFps = 30;
    const mp4Config = await chooseMp4EncoderConfig(
      activeCanvasPreset.width,
      activeCanvasPreset.height,
      exportFps,
    );
    const hasFfmpegFallback = canUseFfmpegFallback();
    const webmRecorderFormat = chooseWebmRecorderFormats()[0] ?? null;

    if (!mp4Config && !hasFfmpegFallback && !webmRecorderFormat) {
      setErrorMessage(
        'This browser cannot record the showcase video locally right now. Try a current version of Chrome, Edge, or Safari.',
      );
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setExportProgress(0);
    setDownloadLabel(
      mp4Config || hasFfmpegFallback ? 'MP4' : (webmRecorderFormat?.label ?? 'Video'),
    );

    try {
      const usagePayload = await authorizeUsage();
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = activeCanvasPreset.width;
      exportCanvas.height = activeCanvasPreset.height;
      const context = exportCanvas.getContext('2d');

      if (!context) {
        throw new Error('The browser could not create a local video export canvas.');
      }

      const totalFrames = Math.max(1, Math.round(totalDuration * exportFps));
      const frameDurationMs = 1000 / exportFps;
      const frameDurationUs = Math.round(1_000_000 / exportFps);
      const renderFrameToCanvas = (frameIndex: number) => {
        const frameTimeSec =
          totalFrames === 1
            ? 0
            : Math.min(frameIndex / exportFps, Math.max(totalDuration - 1 / exportFps, 0));

        renderShowcaseFrame(
          context,
          frameTimeSec,
          slides,
          activeCanvasPreset,
          totalDuration,
          motionStyle,
          transitionStyle,
          backgroundFill,
          headline,
          badge,
          brandOutro,
        );
      };
      const recordWebmWithFormat = async (recorderFormat: RecorderFormat) => {
        const stream = exportCanvas.captureStream(
          recorderFormat.streamMode === 'request-frame' ? 0 : exportFps,
        );
        const captureTrack = stream.getVideoTracks()[0] as
          | (MediaStreamTrack & { requestFrame?: () => void })
          | undefined;
        const canRequestFrame =
          recorderFormat.streamMode === 'request-frame' &&
          Boolean(captureTrack && typeof captureTrack.requestFrame === 'function');
        const recorder = new MediaRecorder(stream, { mimeType: recorderFormat.mimeType });
        const chunks: BlobPart[] = [];

        recorder.ondataavailable = (event) => {
          if (event.data?.size) {
            chunks.push(event.data);
          }
        };

        renderShowcaseFrame(
          context,
          0,
          slides,
          activeCanvasPreset,
          totalDuration,
          motionStyle,
          transitionStyle,
          backgroundFill,
          headline,
          badge,
          brandOutro,
        );
        captureTrack?.requestFrame?.();
        await wait(60);

        const resultBlob = await new Promise<Blob>((resolve, reject) => {
          let completed = false;

          recorder.onerror = () => {
            completed = true;
            stream.getTracks().forEach((track) => track.stop());
            reject(new Error('The browser could not finish the local video recording.'));
          };

          recorder.onstop = () => {
            if (completed) {
              return;
            }

            completed = true;
            stream.getTracks().forEach((track) => track.stop());
            resolve(new Blob(chunks, { type: recorderFormat.mimeType }));
          };

          void (async () => {
            recorder.start(250);

            try {
              for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
                renderFrameToCanvas(frameIndex);

                if (canRequestFrame) {
                  captureTrack?.requestFrame?.();
                }

                setExportProgress((frameIndex + 1) / totalFrames);
                await wait(frameDurationMs);
              }

              await wait(220);
              recorder.requestData?.();
              await wait(180);

              if (recorder.state !== 'inactive') {
                recorder.stop();
              }
            } catch (error) {
              completed = true;

              if (recorder.state !== 'inactive') {
                recorder.stop();
              }

              stream.getTracks().forEach((track) => track.stop());
              reject(
                error instanceof Error
                  ? error
                  : new Error('The browser could not finish the local WebM recording.'),
              );
            }
          })();
        });

        const duration = await loadRecordedVideoDuration(resultBlob);

        return {
          blob: resultBlob,
          duration,
        };
      };
      const encodeMp4WithWebCodecs = async (config: VideoEncoderConfig) => {
        const { ArrayBufferTarget, Muxer } = await import('mp4-muxer');
        const target = new ArrayBufferTarget();
        const muxer = new Muxer({
          target,
          video: {
            codec: 'avc',
            width: activeCanvasPreset.width,
            height: activeCanvasPreset.height,
            frameRate: exportFps,
          },
          fastStart: 'in-memory',
          firstTimestampBehavior: 'strict',
        });

        let encoderError: Error | null = null;

        const encoder = new VideoEncoder({
          output: (chunk, meta) => {
            try {
              muxer.addVideoChunk(chunk, meta);
            } catch (error) {
              encoderError =
                error instanceof Error
                  ? error
                  : new Error('The browser could not mux the local MP4 video.');
            }
          },
          error: (error) => {
            encoderError =
              error instanceof Error
                ? error
                : new Error('The browser could not encode the local MP4 video.');
          },
        });

        try {
          encoder.configure(config);

          for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
            if (encoderError) {
              throw encoderError;
            }

            renderFrameToCanvas(frameIndex);

            const frame = new VideoFrame(exportCanvas, {
              timestamp: frameIndex * frameDurationUs,
              duration: frameDurationUs,
            });

            encoder.encode(frame, {
              keyFrame: frameIndex === 0 || frameIndex % Math.max(exportFps * 2, 1) === 0,
            });
            frame.close();

            if (encoder.encodeQueueSize >= 6) {
              await encoder.flush();
            }

            if (frameIndex % 8 === 0) {
              await yieldToBrowser();
            }

            setExportProgress((frameIndex + 1) / totalFrames);
          }

          await encoder.flush();

          if (encoderError) {
            throw encoderError;
          }

          muxer.finalize();
          const resultBlob = new Blob([target.buffer], { type: 'video/mp4' });
          const duration = await loadRecordedVideoDuration(resultBlob);

          return {
            blob: resultBlob,
            duration,
          };
        } finally {
          if (encoder.state !== 'closed') {
            encoder.close();
          }
        }
      };
      const encodeMp4WithFfmpeg = async () => {
        const { ffmpeg, fetchFile } = await getFfmpegRuntime();
        const progressCallback = ({ progress }: { progress: number }) => {
          setExportProgress(Math.max(0.48, Math.min(0.96, 0.48 + progress * 0.44)));
        };
        const sessionId = createId();
        const framePrefix = `frame-${sessionId}`;
        const frameNames: string[] = [];
        const outputName = `showcase-${sessionId}.mp4`;
        let lastFfmpegError: Error | null = null;

        ffmpeg.on('progress', progressCallback);

        try {
          for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
            renderFrameToCanvas(frameIndex);

            const frameBlob = await canvasToBlob(exportCanvas, 'image/jpeg', 0.92);
            const frameName = `${framePrefix}-${frameIndex.toString().padStart(4, '0')}.jpg`;
            frameNames.push(frameName);
            await ffmpeg.writeFile(frameName, await fetchFile(frameBlob));

            setExportProgress(Math.min(0.46, ((frameIndex + 1) / totalFrames) * 0.46));

            if (frameIndex % 6 === 0) {
              await yieldToBrowser();
            }
          }

          const mp4Commands: string[][] = [
            [
              '-framerate',
              String(exportFps),
              '-i',
              `${framePrefix}-%04d.jpg`,
              '-c:v',
              'libx264',
              '-pix_fmt',
              'yuv420p',
              '-movflags',
              '+faststart',
              '-preset',
              'veryfast',
              '-crf',
              '23',
              outputName,
            ],
            [
              '-framerate',
              String(exportFps),
              '-i',
              `${framePrefix}-%04d.jpg`,
              '-c:v',
              'mpeg4',
              '-q:v',
              '4',
              '-movflags',
              '+faststart',
              outputName,
            ],
          ];

          let commandSucceeded = false;

          for (const args of mp4Commands) {
            const exitCode = await ffmpeg.exec(args);

            if (exitCode === 0) {
              commandSucceeded = true;
              break;
            }

            lastFfmpegError = new Error('Local ffmpeg could not assemble the MP4 showcase.');
          }

          if (!commandSucceeded) {
            throw lastFfmpegError ?? new Error('Local ffmpeg could not assemble the MP4 showcase.');
          }

          const outputData = await ffmpeg.readFile(outputName);
          const outputBytes =
            outputData instanceof Uint8Array
              ? outputData
              : new TextEncoder().encode(String(outputData));
          const outputCopy = new Uint8Array(outputBytes.byteLength);
          outputCopy.set(outputBytes);
          const resultBlob = new Blob([outputCopy], { type: 'video/mp4' });
          const duration = await loadRecordedVideoDuration(resultBlob);

          return {
            blob: resultBlob,
            duration,
          };
        } finally {
          ffmpeg.off('progress', progressCallback);

          await Promise.allSettled(frameNames.map((frameName) => ffmpeg.deleteFile(frameName)));
          await ffmpeg.deleteFile(outputName).catch(() => null);
        }
      };

      let finalBlob: Blob | null = null;
      let finalLabel: 'MP4' | 'WebM' | null = null;
      let finalExtension: 'mp4' | 'webm' | null = null;
      let lastExportError: Error | null = null;

      if (mp4Config) {
        try {
          setDownloadLabel('MP4');
          const attempt = await encodeMp4WithWebCodecs(mp4Config);

          if (isRecordedDurationAcceptable(attempt.duration, totalDuration)) {
            finalBlob = attempt.blob;
            finalLabel = 'MP4';
            finalExtension = 'mp4';
          } else {
            lastExportError = new Error(
              'The local MP4 encoder returned an invalid duration on this device.',
            );
          }
        } catch (error) {
          lastExportError =
            error instanceof Error
              ? error
              : new Error('The browser could not finish the local MP4 export.');
        }
      }

      if (!finalBlob && hasFfmpegFallback) {
        try {
          setDownloadLabel('MP4');
          const attempt = await encodeMp4WithFfmpeg();

          if (isRecordedDurationAcceptable(attempt.duration, totalDuration)) {
            finalBlob = attempt.blob;
            finalLabel = 'MP4';
            finalExtension = 'mp4';
          } else {
            lastExportError = new Error(
              'The local MP4 fallback returned an invalid duration on this device.',
            );
          }
        } catch (error) {
          lastExportError =
            error instanceof Error
              ? error
              : new Error('The browser could not finish the local MP4 fallback export.');
        }
      }

      if (!finalBlob && webmRecorderFormat) {
        setDownloadLabel('WebM');
        const attempt = await recordWebmWithFormat(webmRecorderFormat);

        if (isRecordedDurationAcceptable(attempt.duration, totalDuration)) {
          finalBlob = attempt.blob;
          finalLabel = webmRecorderFormat.label;
          finalExtension = webmRecorderFormat.extension;
        } else {
          lastExportError = new Error(
            'The browser could not produce a stable local video export in MP4 or WebM.',
          );
        }
      }

      if (!finalBlob || !finalLabel || !finalExtension) {
        throw (
          lastExportError ??
          new Error(
            'This browser could not produce a valid local showcase video file. Try Chrome on Mac for MP4, or another browser with stable WebM support.',
          )
        );
      }

      const objectUrl = URL.createObjectURL(finalBlob);
      const exportBaseName = toolMode === 'app-promo' ? 'app-promo' : 'product-showcase';
      const fileName = `${getBaseName(slides[0]?.name || exportBaseName)}-${canvasPresetId}-${durationPreset}s.${finalExtension}`;
      triggerDownload(objectUrl, fileName);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? `Local ${finalLabel} export finished. Pro plan active.`
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Local ${finalLabel} export finished. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : `Local ${finalLabel} export finished.`,
      );
      setExportProgress(1);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'The browser could not finish the showcase export locally.',
      );
    } finally {
      setIsExporting(false);
      window.setTimeout(() => setExportProgress(0), 1200);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local browser processing'}</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step tool-product-prep-step-complete">
          <strong>1. Upload</strong>
          <span>{copy.step1}</span>
        </div>
        <div
          className={`tool-product-prep-step ${slides.length ? 'tool-product-prep-step-active' : ''}`}
        >
          <strong>2. Arrange</strong>
          <span>{copy.step2}</span>
        </div>
        <div
          className={`tool-product-prep-step ${
            slides.length >= minSlides ? 'tool-product-prep-step-active' : ''
          }`}
        >
          <strong>3. Style</strong>
          <span>{copy.step3}</span>
        </div>
        <div
          className={`tool-product-prep-step ${
            slides.length >= minSlides ? 'tool-product-prep-step-active' : ''
          }`}
        >
          <strong>4. Export</strong>
          <span>{copy.step4}</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>{copy.uploadHeading}</strong>
              <span>{copy.uploadSubheading}</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              disabled={isExporting}
              onClick={() => uploadInputRef.current?.click()}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleIncomingFiles(event.dataTransfer.files);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isPreparing
                    ? 'Preparing images locally…'
                    : hasSlides
                      ? `${slides.length} ${copy.uploaderReadyLabel}`
                      : copy.uploaderIdleLabel}
                </strong>
                <span>
                  {hasSlides
                    ? `${slides.length} ${copy.uploaderReadySummary} • ${formatFileSize(slides.reduce((sum, slide) => sum + slide.size, 0))} total`
                    : `2 to ${maxSlides} ${copy.uploaderIdleSummary} • ${Math.round(maxFileSize / (1024 * 1024))} MB each • local only`}
                </span>
              </div>
            </button>
            <input
              ref={uploadInputRef}
              accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
              className="tool-live-file-input"
              multiple
              onChange={(event) => {
                void handleIncomingFiles(event.target.files);
                event.currentTarget.value = '';
              }}
              type="file"
            />
            <input
              ref={replaceInputRef}
              accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
              className="tool-live-file-input"
              onChange={(event) => {
                void handleIncomingFiles(event.target.files, replaceTargetId);
                event.currentTarget.value = '';
              }}
              type="file"
            />

            {hasSlides ? (
              <div className="tool-batch-queue-strip" aria-label="Upload status">
                <span>{`${slides.length} ${copy.queueSummary}`}</span>
                <span>{`${activeCanvasPreset.label} canvas`}</span>
                <span>{`${durationPreset} sec target`}</span>
              </div>
            ) : (
              <div className="tool-editor-empty tool-video-empty">
                <strong>{copy.uploaderEmptyTitle}</strong>
                <span>{copy.uploaderEmptySubtitle}</span>
              </div>
            )}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>{copy.arrangeHeading}</strong>
              <span>{copy.arrangeSubheading}</span>
            </div>

            <div className="tool-live-preview tool-video-preview-shell">
              <div className="tool-live-preview-top">
                <div className="tool-video-preview-heading">
                  <strong>{copy.previewTitle}</strong>
                  <span>
                    {hasSlides
                      ? `${slides.length} ${copy.summarySlidesLabel.toLowerCase()} • ${durationPreset} sec • ${isPreviewPlaying ? 'Live loop' : copy.previewPaused}`
                      : copy.previewEmpty}
                  </span>
                </div>
                <div className="tool-collage-editor-actions">
                  <button
                    className="button button-ghost"
                    disabled={!hasSlides}
                    onClick={() => setIsPreviewPlaying((current) => !current)}
                    type="button"
                  >
                    {isPreviewPlaying ? 'Pause preview' : 'Play preview'}
                  </button>
                  <button
                    className="button button-ghost"
                    disabled={!hasSlides}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div
                ref={previewFrameRef}
                className={`tool-live-preview-frame tool-video-preview-frame ${
                  hasSlides ? '' : 'tool-video-preview-frame-empty'
                } ${toolMode === 'app-promo' ? 'tool-video-preview-frame-square' : ''}`}
              >
                <div
                  className={`tool-video-preview-stage ${
                    hasSlides ? '' : 'tool-video-preview-stage-empty'
                  } ${toolMode === 'app-promo' ? 'tool-video-preview-stage-square' : ''}`}
                >
                  {hasSlides ? (
                    <canvas
                      className="tool-video-preview-canvas"
                      height={activeCanvasPreset.height}
                      ref={previewCanvasRef}
                      width={activeCanvasPreset.width}
                    />
                  ) : null}
                  {!hasSlides ? (
                    <div className="tool-video-preview-overlay">
                      <strong>{copy.previewOverlayTitle}</strong>
                      <span>{copy.previewOverlaySubtitle}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              {hasSlides ? (
                <div className="tool-batch-preview-applied">
                  <span>{activeCanvasPreset.label}</span>
                  <span>{`${durationPreset} sec total`}</span>
                  <span>{`${Math.max(1, slideDuration).toFixed(1)} sec per ${toolMode === 'app-promo' ? 'screen' : 'slide'}`}</span>
                  {toolMode === 'app-promo' ? <span>30 fps</span> : null}
                  <span>{getMotionLabel(motionStyle)}</span>
                  <span>{getTransitionLabel(transitionStyle)}</span>
                </div>
              ) : null}
            </div>

            {hasSlides ? (
              <div className="tool-video-sequence-shell">
                <div className="tool-product-prep-section-heading">
                  <strong>{copy.sequenceHeading}</strong>
                  <span>{copy.sequenceSubheading}</span>
                </div>
                <div className="tool-video-queue">
                  {slides.map((slide, index) => (
                    <button
                      className={`tool-video-queue-item ${
                        selectedSlide?.id === slide.id ? 'tool-video-queue-item-active' : ''
                      }`}
                      key={slide.id}
                      onClick={() => {
                        setSelectedSlideId(slide.id);
                        setIsPreviewPlaying(false);
                      }}
                      type="button"
                    >
                      <img alt={`${slide.name} thumbnail`} src={slide.previewUrl} />
                      <span>{index + 1}</span>
                      <strong>{slide.name}</strong>
                      <small>
                        {selectedSlide?.id === slide.id ? 'Selected' : copy.reviewLabel}
                      </small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {selectedSlide ? (
              <div className="tool-video-slide-card">
                <div className="tool-product-prep-section-heading">
                  <strong>{`${copy.slideCardPrefix} ${selectedSlideIndex + 1}: ${selectedSlide.name}`}</strong>
                  <span>{copy.slideCardSubheading}</span>
                </div>
                <div className="tool-video-slide-actions">
                  <button
                    className="button button-ghost"
                    disabled={selectedSlideIndex === 0}
                    onClick={() => moveSlide(selectedSlide.id, -1)}
                    type="button"
                  >
                    Move earlier
                  </button>
                  <button
                    className="button button-ghost"
                    disabled={selectedSlideIndex === slides.length - 1}
                    onClick={() => moveSlide(selectedSlide.id, 1)}
                    type="button"
                  >
                    Move later
                  </button>
                  <button
                    className="button button-ghost"
                    onClick={() => {
                      setReplaceTargetId(selectedSlide.id);
                      replaceInputRef.current?.click();
                    }}
                    type="button"
                  >
                    Replace image
                  </button>
                  <button
                    className="button button-ghost"
                    onClick={() => removeSlide(selectedSlide.id)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
                <div className="tool-live-controls tool-video-framing-grid">
                  <label className="tool-live-field">
                    <span>{copy.slideZoomLabel}</span>
                    <input
                      max={1.7}
                      min={0.55}
                      onChange={(event) =>
                        updateSelectedSlide({ zoom: Number(event.target.value) })
                      }
                      step={0.01}
                      type="range"
                      value={selectedSlide.zoom}
                    />
                    <small>
                      {selectedSlide.zoom < 1
                        ? `${selectedSlide.zoom.toFixed(2)}× • zoomed out`
                        : `${selectedSlide.zoom.toFixed(2)}×`}
                    </small>
                  </label>
                  <label className="tool-live-field">
                    <span>{copy.slideHorizontalLabel}</span>
                    <input
                      max={0.22}
                      min={-0.22}
                      onChange={(event) =>
                        updateSelectedSlide({ offsetX: Number(event.target.value) })
                      }
                      step={0.01}
                      type="range"
                      value={selectedSlide.offsetX}
                    />
                    <small>
                      {selectedSlide.offsetX > 0
                        ? 'Right'
                        : selectedSlide.offsetX < 0
                          ? 'Left'
                          : 'Centered'}
                    </small>
                  </label>
                  <label className="tool-live-field">
                    <span>{copy.slideVerticalLabel}</span>
                    <input
                      max={0.22}
                      min={-0.22}
                      onChange={(event) =>
                        updateSelectedSlide({ offsetY: Number(event.target.value) })
                      }
                      step={0.01}
                      type="range"
                      value={selectedSlide.offsetY}
                    />
                    <small>
                      {selectedSlide.offsetY > 0
                        ? 'Down'
                        : selectedSlide.offsetY < 0
                          ? 'Up'
                          : 'Centered'}
                    </small>
                  </label>
                </div>
              </div>
            ) : null}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>{copy.styleHeading}</strong>
              <span>{copy.styleSubheading}</span>
            </div>

            <div className="tool-batch-preset-grid">
              {availableTemplatePresets.map((preset) => (
                <button
                  className={`tool-batch-preset-card ${
                    templatePresetId === preset.id ? 'tool-batch-preset-card-active' : ''
                  }`}
                  key={preset.id}
                  onClick={() => applyTemplatePreset(preset.id)}
                  type="button"
                >
                  <strong>{preset.label}</strong>
                  <span>{preset.summary}</span>
                </button>
              ))}
            </div>

            <div className="tool-batch-preset-summary">
              <strong>
                {
                  (availableTemplatePresets.find((preset) => preset.id === templatePresetId) ??
                    availableTemplatePresets[0])!.label
                }
              </strong>
              <span>
                {
                  (availableTemplatePresets.find((preset) => preset.id === templatePresetId) ??
                    availableTemplatePresets[0])!.summary
                }
              </span>
            </div>

            <div className="tool-video-style-shell">
              <div className="tool-video-style-section">
                <div className="tool-product-prep-section-heading">
                  <strong>{copy.coreTitle}</strong>
                  <span>{copy.coreSubtitle}</span>
                </div>
                <div className="tool-live-controls tool-video-style-grid">
                  <label className="tool-live-field tool-live-field-full">
                    <span>Canvas</span>
                    <div className="tool-batch-preset-grid tool-video-chip-grid">
                      {availableCanvasPresets.map((preset) => (
                        <button
                          className={`tool-batch-preset-card ${
                            canvasPresetId === preset.id ? 'tool-batch-preset-card-active' : ''
                          }`}
                          key={preset.id}
                          onClick={() => {
                            setCanvasPresetId(preset.id);
                            resetExportState();
                          }}
                          type="button"
                        >
                          <strong>{preset.label}</strong>
                          <span>{getCanvasSummary(toolMode, preset.id)}</span>
                        </button>
                      ))}
                    </div>
                  </label>

                  <label
                    className={`tool-live-field ${toolMode === 'app-promo' ? '' : 'tool-live-field-full'}`}
                  >
                    <span>Duration</span>
                    <div className="tool-batch-preset-grid tool-video-chip-grid tool-video-chip-grid-compact">
                      {[6, 10, 15].map((preset) => (
                        <button
                          className={`tool-batch-preset-card ${
                            durationPreset === preset ? 'tool-batch-preset-card-active' : ''
                          }`}
                          key={preset}
                          onClick={() => {
                            setDurationPreset(preset as DurationPreset);
                            resetExportState();
                          }}
                          type="button"
                        >
                          <strong>{preset} sec</strong>
                          <span>{`~${Math.max(1, Math.round((preset as number) / Math.max(effectiveSlideCount || 1, 1)))} sec per ${toolMode === 'app-promo' ? 'screen' : 'slide'}`}</span>
                        </button>
                      ))}
                    </div>
                  </label>

                  {toolMode === 'app-promo' ? (
                    <label className="tool-live-field">
                      <span>Fill color</span>
                      <div className="tool-video-fill-control">
                        <input
                          aria-label="Fill color"
                          onChange={(event) => {
                            setBackgroundFill(event.target.value);
                            resetExportState();
                          }}
                          type="color"
                          value={backgroundFill}
                        />
                        <strong>{backgroundFill.toUpperCase()}</strong>
                      </div>
                      <small>Used when a screenshot does not fill the full video frame.</small>
                    </label>
                  ) : null}
                </div>
              </div>

              <div className="tool-video-style-section">
                <div className="tool-product-prep-section-heading">
                  <strong>{copy.motionTitle}</strong>
                  <span>{copy.motionSubtitle}</span>
                </div>
                <div className="tool-live-controls tool-video-style-grid">
                  <label className="tool-live-field tool-live-field-full">
                    <span>Motion</span>
                    <div className="tool-batch-preset-grid tool-video-chip-grid tool-video-chip-grid-motion">
                      {(['still', 'slow-zoom', 'pan', 'zoom-pan'] as MotionStyle[]).map((style) => (
                        <button
                          className={`tool-batch-preset-card ${
                            motionStyle === style ? 'tool-batch-preset-card-active' : ''
                          }`}
                          key={style}
                          onClick={() => {
                            setMotionStyle(style);
                            resetExportState();
                          }}
                          type="button"
                        >
                          <strong>{getMotionLabel(style)}</strong>
                          <span>{getMotionSummary(toolMode, style)}</span>
                        </button>
                      ))}
                    </div>
                  </label>
                </div>
              </div>

              {toolMode === 'app-promo' ? (
                <div className="tool-video-style-section">
                  <div className="tool-video-advanced-top">
                    <div className="tool-product-prep-section-heading">
                      <strong>More settings</strong>
                      <span>Open this only if you need a different transition or promo text.</span>
                    </div>
                    <button
                      className="button button-ghost"
                      onClick={() => setShowAdvancedSettings((current) => !current)}
                      type="button"
                    >
                      {showAdvancedSettings ? 'Hide advanced' : 'Show advanced'}
                    </button>
                  </div>
                  {showAdvancedSettings ? (
                    <div className="tool-video-advanced-body">
                      <div className="tool-live-controls tool-video-style-grid">
                        <label className="tool-live-field tool-live-field-full">
                          <span>Transition</span>
                          <div className="tool-batch-preset-grid tool-video-chip-grid tool-video-chip-grid-compact">
                            {(['cut', 'fade', 'slide'] as TransitionStyle[]).map((style) => (
                              <button
                                className={`tool-batch-preset-card ${
                                  transitionStyle === style ? 'tool-batch-preset-card-active' : ''
                                }`}
                                key={style}
                                onClick={() => {
                                  setTransitionStyle(style);
                                  resetExportState();
                                }}
                                type="button"
                              >
                                <strong>{getTransitionLabel(style)}</strong>
                                <span>{getTransitionSummary(toolMode, style)}</span>
                              </button>
                            ))}
                          </div>
                        </label>
                      </div>

                      <div className="tool-product-prep-section-heading">
                        <strong>{copy.overlaysTitle}</strong>
                        <span>{copy.overlaysSubtitle}</span>
                      </div>
                      <div className="tool-live-controls tool-video-style-grid">
                        <label className="tool-live-field">
                          <span>{copy.headlineLabel}</span>
                          <input
                            maxLength={42}
                            onChange={(event) => {
                              setHeadline(event.target.value);
                              resetExportState();
                            }}
                            placeholder={copy.headlinePlaceholder}
                            type="text"
                            value={headline}
                          />
                        </label>

                        <label className="tool-live-field">
                          <span>{copy.badgeLabelField}</span>
                          <input
                            maxLength={18}
                            onChange={(event) => {
                              setBadge(event.target.value);
                              resetExportState();
                            }}
                            placeholder={copy.badgePlaceholder}
                            type="text"
                            value={badge}
                          />
                        </label>

                        <label className="tool-live-field tool-live-field-full">
                          <span>{copy.outroLabel}</span>
                          <input
                            maxLength={28}
                            onChange={(event) => {
                              setBrandOutro(event.target.value);
                              resetExportState();
                            }}
                            placeholder={copy.outroPlaceholder}
                            type="text"
                            value={brandOutro}
                          />
                          <small>{copy.outroHint}</small>
                        </label>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  <div className="tool-video-style-section">
                    <div className="tool-product-prep-section-heading">
                      <strong>Transition</strong>
                      <span>Choose how one slide hands off to the next.</span>
                    </div>
                    <div className="tool-live-controls tool-video-style-grid">
                      <label className="tool-live-field tool-live-field-full">
                        <span>Transition</span>
                        <div className="tool-batch-preset-grid tool-video-chip-grid tool-video-chip-grid-compact">
                          {(['cut', 'fade', 'slide'] as TransitionStyle[]).map((style) => (
                            <button
                              className={`tool-batch-preset-card ${
                                transitionStyle === style ? 'tool-batch-preset-card-active' : ''
                              }`}
                              key={style}
                              onClick={() => {
                                setTransitionStyle(style);
                                resetExportState();
                              }}
                              type="button"
                            >
                              <strong>{getTransitionLabel(style)}</strong>
                              <span>{getTransitionSummary(toolMode, style)}</span>
                            </button>
                          ))}
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="tool-video-style-section">
                    <div className="tool-product-prep-section-heading">
                      <strong>{copy.overlaysTitle}</strong>
                      <span>{copy.overlaysSubtitle}</span>
                    </div>
                    <div className="tool-live-controls tool-video-style-grid">
                      <label className="tool-live-field">
                        <span>{copy.headlineLabel}</span>
                        <input
                          maxLength={42}
                          onChange={(event) => {
                            setHeadline(event.target.value);
                            resetExportState();
                          }}
                          placeholder={copy.headlinePlaceholder}
                          type="text"
                          value={headline}
                        />
                      </label>

                      <label className="tool-live-field">
                        <span>{copy.badgeLabelField}</span>
                        <input
                          maxLength={18}
                          onChange={(event) => {
                            setBadge(event.target.value);
                            resetExportState();
                          }}
                          placeholder={copy.badgePlaceholder}
                          type="text"
                          value={badge}
                        />
                      </label>

                      <label className="tool-live-field tool-live-field-full">
                        <span>{copy.outroLabel}</span>
                        <input
                          maxLength={28}
                          onChange={(event) => {
                            setBrandOutro(event.target.value);
                            resetExportState();
                          }}
                          placeholder={copy.outroPlaceholder}
                          type="text"
                          value={brandOutro}
                        />
                        <small>{copy.outroHint}</small>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>{copy.exportHeading}</strong>
              <span>{copy.exportSubheading}</span>
            </div>
            <div className="tool-batch-stage-note tool-video-export-note">
              <strong>
                {slides.length < minSlides
                  ? copy.exportLockedLabel
                  : recorderLabel === 'Unavailable'
                    ? 'Local video export is not available here'
                    : `Ready to export ${downloadLabel}`}
              </strong>
              <span>
                {slides.length < minSlides
                  ? copy.exportLockedSubtitle
                  : recorderLabel === 'Unavailable'
                    ? 'Try a current version of Chrome, Edge, or Safari if this device cannot create the showcase video locally right now.'
                    : recorderLabel === 'MP4'
                      ? 'MP4 is preferred for Mac and everyday marketplace uploads. This browser supports direct local MP4 export.'
                      : 'MP4 is not available in this browser, so the export will fall back to WebM locally without changing the workflow.'}
              </span>
            </div>
            {isExporting ? (
              <div className="tool-batch-queue-summary">
                <div className="tool-batch-progress" aria-label="Export progress">
                  <div className="tool-batch-progress-bar">
                    <span style={{ width: `${Math.round(exportProgress * 100)}%` }} />
                  </div>
                  <div className="tool-batch-progress-meta">
                    <strong>Rendering locally…</strong>
                    <span>{`${Math.round(exportProgress * 100)}% • ${durationPreset}s ${downloadLabel} ${copy.exportProgressSuffix}`}</span>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="hero-actions tool-live-actions">
              <button
                className="button"
                disabled={!canExport}
                onClick={() => void exportVideo()}
                type="button"
              >
                {isExporting ? `Rendering ${downloadLabel}…` : `Export ${downloadLabel}`}
              </button>
            </div>
          </div>

          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
          {creditMessage ? <p className="support-feedback">{creditMessage}</p> : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>{copy.summarySlidesLabel}</strong>
              <span>
                {slides.length
                  ? `${slides.length} ${toolMode === 'app-promo' ? 'screen' : 'image'}${slides.length === 1 ? '' : 's'}`
                  : toolMode === 'app-promo'
                    ? 'Add screenshots'
                    : 'Add images'}
              </span>
            </div>
            <div>
              <strong>{copy.summaryTemplateLabel}</strong>
              <span>
                {
                  (availableTemplatePresets.find((preset) => preset.id === templatePresetId) ??
                    availableTemplatePresets[0])!.label
                }
              </span>
            </div>
            <div>
              <strong>Canvas</strong>
              <span>{activeCanvasPreset.label}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>{`${durationPreset} sec`}</span>
            </div>
            {toolMode === 'app-promo' ? (
              <div>
                <strong>Frame rate</strong>
                <span>30 fps</span>
              </div>
            ) : null}
            <div>
              <strong>Motion</strong>
              <span>{getMotionLabel(motionStyle)}</span>
            </div>
            <div>
              <strong>Transition</strong>
              <span>{getTransitionLabel(transitionStyle)}</span>
            </div>
            <div>
              <strong>Overlays</strong>
              <span>{overlaysEnabled ? 'On' : 'Off'}</span>
            </div>
            <div>
              <strong>{copy.summarySelectedLabel}</strong>
              <span>
                {selectedSlide
                  ? `${copy.slideCardPrefix} ${selectedSlideIndex + 1}`
                  : copy.summaryEmptySelected}
              </span>
            </div>
            <div>
              <strong>Export format</strong>
              <span>
                {recorderLabel === 'MP4'
                  ? 'MP4 preferred'
                  : recorderLabel === 'WebM'
                    ? 'WebM fallback'
                    : 'Unavailable'}
              </span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local only</strong>
            <span>{copy.localOnlySubtitle}</span>
          </div>
          {copy.showScopeNote === false ? null : (
            <div className="tool-live-panel-note">
              <strong>{copy.scopeTitle}</strong>
              <span>{copy.scopeSubtitle}</span>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
